/**
 * BỘ HOẠT ẢNH RA CHIÊU — mỗi chiêu một dáng, khớp với TÊN chiêu.
 *
 * Trước đây mọi chiêu dùng chung một cú vung tay như đòn đánh thường ⇒ nhàm.
 * Giờ mỗi chiêu trỏ tới 1 dáng trong bảng này (xem `SkillDef.anim`).
 *
 * `dur`  : thời gian diễn hoạt ảnh (giây mô phỏng)
 * `weight`: độ "nặng" — dùng để rung màn hình và chọn cỡ hiệu ứng
 * `fx`   : kiểu hiệu ứng kèm theo (xem render/fx.ts)
 */
export type CastAnimId =
  | 'palmPush'
  | 'uppercut'
  | 'groundSlam'
  | 'stomp'
  | 'taunt'
  | 'shout'
  | 'volley'
  | 'summon'
  | 'guard'
  | 'dash'
  | 'spin'
  | 'powerUp'
  | 'reload'
  | 'hurl'
  | 'crouch'
  | 'kickCombo'
  | 'punchCombo'
  // ---- 5 dáng "đại chiêu" thêm sau (xem BONUS ở src/art/cast-anims.ts)
  | 'dragonKick'
  | 'meditate'
  | 'fanSweep'
  | 'spearHand'
  | 'doubleFist';

export type CastFx = 'shock' | 'beam' | 'aura' | 'swirl' | 'none';

export interface CastAnimDef {
  /** tên tiếng Việt để hiện trong trò chơi */
  name: string;
  dur: number;
  weight: number;
  fx: CastFx;
  /** màu hiệu ứng */
  color: number;
  /**
   * true = hai tay đưa RA TRƯỚC ⇒ phải vẽ tay ĐÈ LÊN THÂN.
   * Nếu không, tay trái chĩa tới sẽ chui ra sau thân và biến mất (tay vẽ z=25 < thân z=30).
   */
  frontArms?: boolean;
}

export const CAST_ANIMS: Record<CastAnimId, CastAnimDef> = {
  /** Chưởng: hai tay đẩy thẳng tới, người lao về trước */
  palmPush: { name: 'Chưởng', dur: 0.52, weight: 3, fx: 'beam', color: 0x60a5fa, frontArms: true },
  /** Thượng quyền: tay phải móc từ dưới lên, người bật cao */
  uppercut: { name: 'Thượng Quyền', dur: 0.46, weight: 3, fx: 'shock', color: 0xfbbf24 },
  /** Đập đất: hai tay giơ cao rồi giáng xuống, người sụp xuống */
  groundSlam: { name: 'Đập Đất', dur: 0.72, weight: 5, fx: 'shock', color: 0xf97316, frontArms: true },
  /** Đạp đất: một chân nhấc cao rồi dậm xuống */
  stomp: { name: 'Dậm Đất', dur: 0.6, weight: 4, fx: 'shock', color: 0xfacc15 },
  /** Khiêu khích: hai tay mở rộng, ưỡn ngực, ngả người ra sau */
  taunt: { name: 'Khiêu Khích', dur: 0.7, weight: 2, fx: 'aura', color: 0xfacc15 },
  /** Gào: hai tay chụm quanh miệng, đầu chúi tới, người rung */
  shout: { name: 'Gào', dur: 0.62, weight: 2, fx: 'swirl', color: 0xa78bfa, frontArms: true },
  /** Ném liên tiếp: hai tay ném xen kẽ rất nhanh (4 nhịp) */
  volley: { name: 'Ném Liên Tiếp', dur: 0.55, weight: 2, fx: 'none', color: 0x86efac, frontArms: true },
  /** Triệu hồi: hai tay giơ lên trời, người ưỡn ra sau */
  summon: { name: 'Triệu Hồi', dur: 0.85, weight: 3, fx: 'swirl', color: 0xf472b6 },
  /** Thủ: hai tay bắt chéo trước ngực, hạ thấp người */
  guard: { name: 'Thủ Thế', dur: 0.6, weight: 2, fx: 'aura', color: 0x38bdf8, frontArms: true },
  /** Lướt: người lao tới, hai tay giật về sau */
  dash: { name: 'Lướt', dur: 0.38, weight: 2, fx: 'beam', color: 0x67e8f9 },
  /** Xoay đánh nhiều đòn: thân xoay qua lại liên tục */
  spin: { name: 'Liên Hoàn', dur: 0.8, weight: 3, fx: 'swirl', color: 0xa78bfa },
  /** Bùng sức mạnh: hai tay gồng ra hai bên, người rung bần bật */
  powerUp: { name: 'Bùng Sức', dur: 0.75, weight: 3, fx: 'aura', color: 0xfbbf24, frontArms: true },
  /** Nạp đạn: hai tay thao tác ngang hông */
  reload: { name: 'Nạp Đạn', dur: 0.5, weight: 1, fx: 'none', color: 0xfde68a, frontArms: true },
  /** Ném mạnh: vung tay qua đầu rồi ném mạnh về trước */
  hurl: { name: 'Ném Mạnh', dur: 0.58, weight: 3, fx: 'beam', color: 0xfca5a5 },
  /** Hạ thấp: ngồi sụp xuống, hai tay chạm đất rồi bật lên */
  crouch: { name: 'Ẩn Thân', dur: 0.55, weight: 1, fx: 'swirl', color: 0x94a3b8, frontArms: true },
  /**
   * ĐÁ LIÊN HOÀN: 4 cú đá xen kẽ hai chân, biên độ LỚN.
   * (Dáng `spin` cũ chỉ xoay hông ±0,5 rad ≈ 29° nên nhìn không ra cú đá —
   *  đây chính là lý do người chơi phàn nàn "không thấy đá liên hoàn".)
   */
  kickCombo: { name: 'Đá Liên Hoàn', dur: 0.8, weight: 3, fx: 'shock', color: 0x67e8f9 },
  /** ĐẤM LIÊN HOÀN: 6 cú đấm xen kẽ hai tay thật nhanh. */
  punchCombo: { name: 'Đấm Liên Hoàn', dur: 0.7, weight: 3, fx: 'aura', color: 0xfbbf24 },
  /*
   * ────────────────────────────────────────────────────────────────────────
   * 5 DÁNG "ĐẠI CHIÊU" — thêm sau, khi nâng cấp võ thuật.
   *
   * ⚠️ FILE NÀY LÀ NGUỒN SỰ THẬT của metadata (tên · dur · weight · fx · màu ·
   * frontArms). Hình dáng xương nằm ở `src/art/cast-anims.ts` và file đó CHỈ ĐỌC
   * bảng này (`BONUS_CAST_ANIMS` ở đó là bí danh trỏ về đây) ⇒ không có 2 bản
   * metadata lệch nhau, cũng không có vòng import.
   *
   * `frontArms: true` là BẮT BUỘC với dáng hai tay đưa ra trước — thiếu là tay trái
   * (z=25) chui ra sau thân (z=30) và biến mất.
   */
  /** Đá xoay trọn một vòng rồi duỗi chân ở đỉnh vòng xoay */
  dragonKick: { name: 'Thần Long Bãi Vĩ', dur: 0.82, weight: 4, fx: 'shock', color: 0x38bdf8 },
  /** Ngồi kiết già tĩnh tâm rồi bùng khí, hai tay mở ra hai bên */
  meditate: { name: 'Thiền Bùng Khí', dur: 0.9, weight: 3, fx: 'aura', color: 0xfde68a, frontArms: true },
  /** Quạt ngang liên tục 3 vòng, cả người xoay theo tay */
  fanSweep: { name: 'Phiến Quét', dur: 0.7, weight: 3, fx: 'swirl', color: 0x86efac },
  /** Chọt hai tay thẳng tới như ngọn giáo, người phóng theo */
  spearHand: { name: 'Thương Thủ', dur: 0.5, weight: 3, fx: 'beam', color: 0xfca5a5, frontArms: true },
  /** Hai nắm đấm thụt về hông rồi cùng phóng ra trước */
  doubleFist: { name: 'Song Quyền', dur: 0.48, weight: 3, fx: 'shock', color: 0xfbbf24, frontArms: true },
};

export const CAST_ANIM_IDS = Object.keys(CAST_ANIMS) as CastAnimId[];

export function animOf(id: string): CastAnimDef {
  return CAST_ANIMS[id as CastAnimId] ?? CAST_ANIMS.palmPush;
}
